import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useAccessibilityCompliance } from 'hooks/useAccessibilityCompliance';
import { useAccessibilityComplianceV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { trackProjectMaterials } from 'utils/analytics';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { formatProjectCost } from 'utils/currency';
import { formatDate } from 'utils/date';

export const AccessibilityCompliance = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result:
    | ReturnType<typeof useAccessibilityComplianceV4>
    | ReturnType<typeof useAccessibilityCompliance>;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useAccessibilityComplianceV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('accessibilityCompliance');
    resolvedDocumentId = documentUuid || undefined;
    result = useAccessibilityCompliance(documentUuid || undefined);
  }
  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const relatedContacts = useMemo(() => {
    if (!result.data?.metadata) return null;

    const { metadata } = result.data;
    let contacts = metadata.relatedcontacts || [];

    // Add person filing contact if it exists
    if (metadata.person_filing_contact_name) {
      contacts = [
        ...contacts,
        {
          type: 'Applicant',
          name: metadata.person_filing_contact_name,
          phone: '',
          company: '',
          email: '',
          mailing_addresses: [],
          physical_addresses: [],
          license_number: '',
          same_as: null,
          isperson: true,
          isorganization: false,
        },
      ];
    }

    return parseRelatedContactsIntoList(contacts);
  }, [result.data?.metadata]);

  const permitUrl = result.data?.document_url;

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Accessibility Compliance">
          {result.data?.metadata?.permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={result.data?.metadata?.parcel_address ?? ''}
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.project_name ? (
                <Text fontWeight="bold">{result.data.metadata.project_name}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Scope of Work</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.description || <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Facility Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.design_firm_name || <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Project Value</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.estprojectcost ? (
                <Text>{formatProjectCost(result.data.metadata.estprojectcost)}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Est. Start Date</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.eststartdate ? (
                <Text>
                  {formatDate(
                    result.data.metadata.eststartdate.replace(' ', 'T'),
                    'MMM dd, yyyy',
                  )}
                </Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Est. Completion Date</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.estcompletiondate ? (
                <Text>
                  {formatDate(
                    result.data.metadata.estcompletiondate.replace(' ', 'T'),
                    'MMM dd, yyyy',
                  )}
                </Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Type of Funds</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.type_of_funds || <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Work Type</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.workclass || <EmptyField />}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Project Sqft</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.totalsqft || <EmptyField />} sq. ft.
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <ModalStatus activities={result.data?.activities} Skeleton={Skeleton} />

        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}

        {!result.isLoading && permitUrl && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <ExternalLink
                to={permitUrl}
                onClick={() => trackProjectMaterials(permitUrl)}
              >
                View application
              </ExternalLink>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
